import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { vehicleService } from '../../services/vehicleService';
import { leadsAPI } from '../../lib/supabase';

const { 
  FiTrendingUp, FiUsers, FiDollarSign, FiBarChart2, 
  FiActivity, FiCalendar, FiShoppingCart, FiPercent 
} = FiIcons;

const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month');
  const [analyticsData, setAnalyticsData] = useState({
    salesTrends: [],
    inventoryMetrics: [],
    leadSources: [],
    conversionFunnel: [],
    monthlyRevenue: [],
    topVehicles: []
  });

  const [kpis, setKpis] = useState({
    totalRevenue: 0,
    totalSales: 0,
    averageDealSize: 0,
    conversionRate: 0,
    inventoryTurnover: 0,
    leadToSaleTime: 0
  });

  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Load vehicles and leads data
      const [vehicles, leads] = await Promise.all([
        vehicleService.getVehicles(),
        leadsAPI.getLeads()
      ]);

      // Generate sample analytics data
      const salesTrends = generateSalesTrends();
      const inventoryMetrics = generateInventoryMetrics(vehicles);
      const leadSources = generateLeadSources();
      const conversionFunnel = generateConversionFunnel();
      const monthlyRevenue = generateMonthlyRevenue();
      const topVehicles = generateTopVehicles(vehicles);

      setAnalyticsData({
        salesTrends,
        inventoryMetrics,
        leadSources,
        conversionFunnel,
        monthlyRevenue,
        topVehicles
      });

      // Calculate KPIs
      calculateKPIs(vehicles, leads);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSalesTrends = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    return months.map(month => ({
      month,
      sales: Math.floor(Math.random() * 30) + 10,
      revenue: Math.floor(Math.random() * 500000) + 200000,
      units: Math.floor(Math.random() * 25) + 5
    }));
  };

  const generateInventoryMetrics = (vehicles) => {
    const types = ['New', 'Used', 'professional'];
    return types.map(type => ({
      type,
      count: Math.floor(Math.random() * 50) + 10,
      value: Math.floor(Math.random() * 1000000) + 500000,
      avgAge: Math.floor(Math.random() * 60) + 30
    }));
  };

  const generateLeadSources = () => {
    return [
      { name: 'Website', value: 35, color: '#3B82F6' },
      { name: 'Walk-in', value: 25, color: '#10B981' },
      { name: 'Phone', value: 20, color: '#F59E0B' },
      { name: 'Referral', value: 15, color: '#8B5CF6' },
      { name: 'Social Media', value: 5, color: '#EF4444' }
    ];
  };

  const generateConversionFunnel = () => {
    return [
      { stage: 'Leads', count: 500, conversion: 100 },
      { stage: 'Qualified', count: 350, conversion: 70 },
      { stage: 'Test Drive', count: 200, conversion: 40 },
      { stage: 'Negotiation', count: 120, conversion: 24 },
      { stage: 'Closed', count: 60, conversion: 12 }
    ];
  };

  const generateMonthlyRevenue = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    return months.map(month => ({
      month,
      new: Math.floor(Math.random() * 300000) + 100000,
      used: Math.floor(Math.random() * 200000) + 50000,
      service: Math.floor(Math.random() * 50000) + 10000,
      parts: Math.floor(Math.random() * 30000) + 5000
    }));
  };

  const generateTopVehicles = (vehicles) => {
    return vehicles.slice(0, 5).map(vehicle => ({
      name: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
      views: Math.floor(Math.random() * 500) + 100,
      inquiries: Math.floor(Math.random() * 50) + 10,
      testDrives: Math.floor(Math.random() * 20) + 5
    }));
  };

  const calculateKPIs = (vehicles, leads) => {
    const totalRevenue = Math.floor(Math.random() * 2000000) + 500000;
    const totalSales = Math.floor(Math.random() * 50) + 20;
    const averageDealSize = Math.floor(totalRevenue / totalSales);
    const conversionRate = ((Math.random() * 20) + 5).toFixed(1);
    const inventoryTurnover = ((Math.random() * 5) + 2).toFixed(1);
    const leadToSaleTime = Math.floor(Math.random() * 10) + 3;

    setKpis({
      totalRevenue,
      totalSales,
      averageDealSize,
      conversionRate,
      inventoryTurnover,
      leadToSaleTime
    });
  };

  const KPICard = ({ icon: Icon, title, value, subtitle, trend }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-sm p-6"
    >
      <div className="flex items-center justify-between mb-2">
        <SafeIcon icon={Icon} className="text-2xl text-primary-600" />
        {trend && (
          <span className={`text-sm font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
      <p className="text-sm text-gray-600">{title}</p>
      {subtitle && (
        <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
      )}
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
        <div className="flex gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KPICard
          icon={FiDollarSign}
          title="Total Revenue"
          value={`$${kpis.totalRevenue.toLocaleString()}`}
          trend={12.5}
        />
        <KPICard
          icon={FiShoppingCart}
          title="Total Sales"
          value={kpis.totalSales}
          subtitle="Units sold"
          trend={8.3}
        />
        <KPICard
          icon={FiTrendingUp}
          title="Avg Deal Size"
          value={`$${kpis.averageDealSize.toLocaleString()}`}
          trend={-2.1}
        />
        <KPICard
          icon={FiPercent}
          title="Conversion Rate"
          value={`${kpis.conversionRate}%`}
          subtitle="Lead to sale"
          trend={5.7}
        />
        <KPICard
          icon={FiActivity}
          title="Inventory Turnover"
          value={`${kpis.inventoryTurnover}x`}
          subtitle="Per month"
        />
        <KPICard
          icon={FiCalendar}
          title="Lead Time"
          value={`${kpis.leadToSaleTime} days`}
          subtitle="Avg to close"
          trend={-15.2}
        />
      </div>

      {/* Sales Trends Chart */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Sales Trends</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={analyticsData.salesTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="sales" stroke="#3B82F6" name="Units Sold" />
            <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#10B981" name="Revenue ($)" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Sources Pie Chart */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Lead Sources</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analyticsData.leadSources}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {analyticsData.leadSources.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Conversion Funnel */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Conversion Funnel</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analyticsData.conversionFunnel}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="stage" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#3B82F6" name="Count" />
              <Bar dataKey="conversion" fill="#10B981" name="Conversion %" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Revenue Breakdown */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Breakdown</h2>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={analyticsData.monthlyRevenue}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="new" stackId="1" stroke="#3B82F6" fill="#3B82F6" name="New Vehicles" />
            <Area type="monotone" dataKey="used" stackId="1" stroke="#10B981" fill="#10B981" name="Used Vehicles" />
            <Area type="monotone" dataKey="service" stackId="1" stroke="#F59E0B" fill="#F59E0B" name="Service" />
            <Area type="monotone" dataKey="parts" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" name="Parts" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Top Performing Vehicles */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Vehicles</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Views
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inquiries
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test Drives
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversion Rate
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analyticsData.topVehicles.map((vehicle, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {vehicle.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {vehicle.views}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {vehicle.inquiries}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {vehicle.testDrives}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {((vehicle.testDrives / vehicle.views) * 100).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
